import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'sonner';

export interface Notification {
  id: string;
  type: 'workflow_review_request' | 'workflow_approved' | 'workflow_rejected' | 'bonus_awarded' | 'system' | 'info';
  title: string;
  message: string;
  from?: string;
  to?: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  metadata?: {
    workflowId?: string;
    workflowName?: string;
    bonusAmount?: number;
    voucherId?: string;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotification: (id: string) => void;
  clearAll: () => void;
  sendWorkflowReviewRequest: (employeeName: string, workflowName: string, workflowId: string, toAdmin: string) => void;
  sendWorkflowApproval: (employeeName: string, workflowName: string, workflowId: string, bonusAmount: number, voucherId: string) => void;
  sendWorkflowRejection: (employeeName: string, workflowName: string, workflowId: string, reason: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: 'welcome-1',
      type: 'system',
      title: 'Bienvenido a ZAPHUB',
      message: 'Sistema de automatizaciones listo para usar',
      timestamp: new Date(),
      read: false,
    }
  ]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false,
    };

    setNotifications(prev => [newNotification, ...prev]);

    // Toast según el tipo
    switch (notification.type) {
      case 'workflow_review_request':
        toast.info(notification.title, { description: notification.message });
        break;
      case 'workflow_approved':
        toast.success(notification.title, { description: notification.message });
        break;
      case 'workflow_rejected':
        toast.error(notification.title, { description: notification.message });
        break;
      case 'bonus_awarded':
        toast.success(notification.title, { description: notification.message, duration: 5000 });
        break;
      default:
        toast(notification.title, { description: notification.message });
    }

    return newNotification.id;
  }, []);

  const sendWorkflowReviewRequest = useCallback((employeeName: string, workflowName: string, workflowId: string, toAdmin: string) => {
    addNotification({
      type: 'workflow_review_request',
      title: '🔔 Solicitud de Revisión',
      message: `${employeeName} solicita revisión del workflow "${workflowName}"`,
      from: employeeName,
      to: toAdmin,
      actionUrl: `/workflows/${workflowId}`,
      metadata: {
        workflowId,
        workflowName,
      },
    });
  }, [addNotification]);

  const sendWorkflowApproval = useCallback((employeeName: string, workflowName: string, workflowId: string, bonusAmount: number, voucherId: string) => {
    addNotification({
      type: 'workflow_approved',
      title: '✅ Workflow Aprobado',
      message: `Tu workflow "${workflowName}" ha sido aprobado. ¡Bonificación de $${bonusAmount} USD agregada a tu nómina!`,
      to: employeeName,
      from: 'Admin',
      actionUrl: `/bonus/${voucherId}`,
      metadata: {
        workflowId,
        workflowName,
        bonusAmount,
        voucherId,
      },
    });
  }, [addNotification]);

  const sendWorkflowRejection = useCallback((employeeName: string, workflowName: string, workflowId: string, reason: string) => {
    addNotification({
      type: 'workflow_rejected',
      title: '❌ Workflow Rechazado',
      message: `Tu workflow "${workflowName}" necesita correcciones. Motivo: ${reason}`,
      to: employeeName,
      from: 'Admin',
      actionUrl: `/workflows/${workflowId}`,
      metadata: {
        workflowId,
        workflowName,
      },
    });
  }, [addNotification]);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const clearNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotification,
        clearAll,
        sendWorkflowReviewRequest,
        sendWorkflowApproval,
        sendWorkflowRejection,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
